/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – MaintenancePanel.tsx FINAL
// Adăugat: Calendar Gantt intervenții + toate statusurile
// ============================================================

import React, { useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, message, Popconfirm } from 'antd';
import {
  ToolOutlined, PlusOutlined, CheckCircleOutlined,
  ClockCircleOutlined, AlertOutlined, CalendarOutlined,
  UserOutlined, EnvironmentOutlined, EditOutlined,
  ExclamationCircleOutlined, FireOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { mockInterventions, mockSensors } from '../../data/mockData';
import { Intervention, InterventionType, InterventionStatus } from '../../types';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;
const { TextArea } = Input;

// ─── Constante ────────────────────────────────────────────

const TYPE_LABEL: Record<InterventionType, string> = {
  calibrare:  'Calibrare',
  reparatie:  'Reparație',
  inlocuire:  'Înlocuire',
  inspectie:  'Inspecție',
  curatare:   'Curățare',
};
const TYPE_COLOR: Record<InterventionType, string> = {
  calibrare: '#6366f1',
  reparatie: '#ef4444',
  inlocuire: '#f97316',
  inspectie: '#06b6d4',
  curatare:  '#10b981',
};

const STAT_LABEL: Record<InterventionStatus, string> = {
  planificata: 'Planificată',
  in_curs:     'În curs',
  finalizata:  'Finalizată',
  anulata:     'Anulată',
};
const STAT_COLOR: Record<InterventionStatus, string> = {
  planificata: '#6366f1',
  in_curs:     '#f59e0b',
  finalizata:  '#10b981',
  anulata:     '#94a3b8',
};
const STAT_BG: Record<InterventionStatus, string> = {
  planificata: '#eef2ff',
  in_curs:     '#fef3c7',
  finalizata:  '#d1fae5',
  anulata:     '#f8fafc',
};

const PRIO_COLOR: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#10b981',
};
const PRIO_LABEL: Record<string, string> = {
  critical: 'Critic', high: 'Ridicat', medium: 'Mediu', low: 'Scăzut',
};

// ─── Gantt mini ───────────────────────────────────────────

const GanttView: React.FC<{ interventions: Intervention[] }> = ({ interventions }) => {
  const now      = Date.now();
  const viewDays = 30;
  const start    = now - 5 * 86400000; // 5 zile în trecut
  const end      = start + viewDays * 86400000;

  const days = Array.from({ length: viewDays }, (_, i) => {
    const d = new Date(start + i * 86400000);
    return { label: d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' }), ts: start + i * 86400000 };
  });

  const toX = (ts: number) => Math.max(0, Math.min(100, ((ts - start) / (end - start)) * 100));

  const relevant = interventions.filter(iv => {
    const s = new Date(iv.scheduledAt).getTime();
    const e = iv.finishedAt ? new Date(iv.finishedAt).getTime() : s + iv.estimatedDuration * 3600000;
    return e >= start && s <= end;
  });

  const todayX = toX(now);

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#6366f1' }}>
          <CalendarOutlined />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Calendar intervenții — 30 zile</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>Vizualizare Gantt · Linie albastră = azi</div>
        </div>
      </div>

      <div style={{ padding: '16px 18px', overflowX: 'auto' }}>
        {/* Header zile */}
        <div style={{ display: 'flex', marginBottom: 12, position: 'relative', height: 24 }}>
          {/* Today marker header */}
          <div style={{ position: 'absolute', left: `calc(${todayX}% + 140px)`, top: 0, bottom: -8, width: 1, background: '#6366f1', zIndex: 5 }} />

          <div style={{ width: 140, flexShrink: 0, fontSize: 10, color: '#94a3b8', fontWeight: 700, display: 'flex', alignItems: 'center' }}>SENZOR</div>
          <div style={{ flex: 1, position: 'relative' }}>
            {days.filter((_, i) => i % 3 === 0).map((d, i) => (
              <span key={i} style={{
                position: 'absolute', left: `${toX(d.ts)}%`,
                fontSize: 9, transform: 'translateX(-50%)',
                fontWeight: d.ts <= now && d.ts + 3 * 86400000 > now ? 700 : 400,
                color: d.ts <= now && d.ts + 3 * 86400000 > now ? '#6366f1' : '#94a3b8',
              }}>
                {d.label}
              </span>
            ))}
          </div>
        </div>

        {/* Rows */}
        {relevant.length === 0 && (
          <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: 12 }}>
            Nicio intervenție în perioada vizualizată
          </div>
        )}
        {relevant.map((iv, idx) => {
          const sTs = new Date(iv.scheduledAt).getTime();
          const eTs = iv.finishedAt ? new Date(iv.finishedAt).getTime() : sTs + iv.estimatedDuration * 3600000;
          const x1  = toX(sTs);
          const x2  = toX(eTs);
          const w   = Math.max(0.5, x2 - x1);
          const color = TYPE_COLOR[iv.type];

          return (
            <div key={iv.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, height: 28 }}>
              <div style={{ width: 140, flexShrink: 0, fontSize: 11, color: '#475569', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
                {iv.sensorName}
              </div>
              <div style={{ flex: 1, position: 'relative', height: '100%', background: '#f8fafc', borderRadius: 4 }}>
                {/* Today line */}
                <div style={{ position: 'absolute', left: `${todayX}%`, top: 0, bottom: 0, width: 1, background: '#6366f185', zIndex: 2 }} />

                {/* Bar */}
                <div style={{
                  position: 'absolute',
                  left: `${x1}%`, width: `${w}%`,
                  top: '15%', height: '70%',
                  background: iv.status === 'finalizata' ? '#10b981' : iv.status === 'in_curs' ? '#f59e0b' : iv.status === 'anulata' ? '#94a3b8' : color,
                  borderRadius: 4,
                  display: 'flex', alignItems: 'center', overflow: 'hidden',
                  boxShadow: `0 1px 4px ${color}40`,
                  cursor: 'default',
                  opacity: iv.status === 'anulata' ? 0.5 : 1,
                }}>
                  <span style={{ fontSize: 9, color: '#fff', fontWeight: 700, padding: '0 5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {TYPE_LABEL[iv.type]}
                  </span>
                </div>
              </div>

              {/* Status badge */}
              <div style={{ width: 90, flexShrink: 0, marginLeft: 8, textAlign: 'right' }}>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 99, background: STAT_BG[iv.status], color: STAT_COLOR[iv.status] }}>
                  {STAT_LABEL[iv.status]}
                </span>
              </div>
            </div>
          );
        })}

        {/* Legend */}
        <div style={{ display: 'flex', gap: 14, marginTop: 14, paddingTop: 12, borderTop: '1px dashed #f1f5f9', flexWrap: 'wrap' }}>
          {(Object.keys(TYPE_LABEL) as InterventionType[]).map(t => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 8, borderRadius: 2, background: TYPE_COLOR[t] }} />
              <span style={{ fontSize: 10, color: '#64748b' }}>{TYPE_LABEL[t]}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
            <div style={{ width: 2, height: 12, background: '#6366f1' }} />
            <span style={{ fontSize: 10, color: '#94a3b8' }}>Azi</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Card intervenție ─────────────────────────────────────

const InterventionCard: React.FC<{
  iv: Intervention; onStart: (id: string) => void; onFinish: (id: string) => void;
}> = ({ iv, onStart, onFinish }) => {
  const color   = TYPE_COLOR[iv.type];
  const sensor  = mockSensors.find(s => s.id === iv.sensorId);
  const isPast  = new Date(iv.scheduledAt) < new Date() && iv.status === 'planificata';

  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: `1px solid ${iv.status === 'in_curs' ? '#fde68a' : iv.status === 'finalizata' ? '#bbf7d0' : '#e2e8f0'}`,
      padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color }}>
            <ToolOutlined />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{iv.sensorName}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{sensor?.location.label}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: `${color}15`, color }}>{TYPE_LABEL[iv.type]}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: STAT_BG[iv.status], color: STAT_COLOR[iv.status] }}>{STAT_LABEL[iv.status]}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: `${PRIO_COLOR[iv.priority]}15`, color: PRIO_COLOR[iv.priority] }}>{PRIO_LABEL[iv.priority]}</span>
        </div>
      </div>

      {/* Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 5 }}>
          <CalendarOutlined style={{ fontSize: 10, marginTop: 1 }} />
          {new Date(iv.scheduledAt).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
          {isPast && <span style={{ color: '#ef4444', fontWeight: 600 }}>· Întârziat!</span>}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 5 }}>
          <UserOutlined style={{ fontSize: 10, marginTop: 1 }} />
          {iv.technician}
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          ⏱ {iv.estimatedDuration}h estimat
        </div>
        {iv.finishedAt && (
          <div style={{ fontSize: 11, color: '#10b981' }}>
            ✓ Finalizat {new Date(iv.finishedAt).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {iv.notes && (
        <div style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', borderRadius: 7, padding: '7px 10px', marginBottom: 10, fontStyle: 'italic' }}>
          {iv.notes}
        </div>
      )}

      {/* Actions */}
      {iv.status === 'planificata' && (
        <button onClick={() => onStart(iv.id)} style={{ width: '100%', padding: '7px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid #fde68a', background: '#fefce8', color: '#d97706', cursor: 'pointer', fontFamily: 'inherit' }}>
          Începe intervenție
        </button>
      )}
      {iv.status === 'in_curs' && (
        <button onClick={() => onFinish(iv.id)} style={{ width: '100%', padding: '7px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontFamily: 'inherit' }}>
          Finalizează intervenția
        </button>
      )}
    </div>
  );
};

// ─── Modal adăugare ───────────────────────────────────────

const AddModal: React.FC<{ open: boolean; onClose: () => void; onAdd: (iv: Intervention) => void }> = ({ open, onClose, onAdd }) => {
  const [form] = Form.useForm();

  const handleOk = () => {
    form.validateFields().then(vals => {
      const sensor = mockSensors.find(s => s.id === vals.sensorId);
      const iv: Intervention = {
        id:                `i-${Date.now()}`,
        sensorId:          vals.sensorId,
        sensorName:        sensor?.name ?? vals.sensorId,
        type:              vals.type,
        status:            'planificata',
        priority:          vals.priority,
        estimatedDuration: vals.duration,
        scheduledAt:       vals.date.toISOString(),
        technician:        vals.technician,
        notes:             vals.notes,
      };
      onAdd(iv);
      message.success('Intervenție planificată cu succes!');
      form.resetFields();
      onClose();
    });
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={handleOk} title="Planifică intervenție nouă" okText="Salvează" cancelText="Anulează" width={500}>
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="sensorId" label="Senzor" rules={[{ required: true }]}>
          <Select placeholder="Selectează senzor…" showSearch optionFilterProp="children">
            {mockSensors.map(s => <Option key={s.id} value={s.id}>{s.name} — {s.location.label}</Option>)}
          </Select>
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="type" label="Tip intervenție" rules={[{ required: true }]}>
            <Select placeholder="Tip…">
              {(Object.keys(TYPE_LABEL) as InterventionType[]).map(t => <Option key={t} value={t}>{TYPE_LABEL[t]}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="priority" label="Prioritate" rules={[{ required: true }]} initialValue="medium">
            <Select>
              {Object.entries(PRIO_LABEL).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
            </Select>
          </Form.Item>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="date" label="Data planificată" rules={[{ required: true }]}>
            <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration" label="Durată estimată (ore)" rules={[{ required: true }]} initialValue={1}>
            <InputNumber min={0.5} max={24} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <Form.Item name="technician" label="Tehnician" rules={[{ required: true }]}>
          <Input prefix={<UserOutlined />} placeholder="Nume tehnician…" />
        </Form.Item>
        <Form.Item name="notes" label="Notițe">
          <TextArea rows={3} placeholder="Descriere lucrări, materiale necesare…" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Panel principal ──────────────────────────────────────

const MaintenancePanel: React.FC = () => {
  const { user, logActivity } = useAuth();
  const [interventions, setInterventions] = useState<Intervention[]>(mockInterventions);
  const [addOpen,  setAddOpen]   = useState(false);
  const [view,     setView]      = useState<'list' | 'gantt'>('list');
  const [filterSt, setFilterSt]  = useState<InterventionStatus | 'all'>('all');

  const start  = (id: string) => {
    setInterventions(p => p.map(iv => iv.id === id ? { ...iv, status: 'in_curs' } : iv));
    logActivity('Intervenție începută', 'Mentenanță', id);
    message.success('Intervenție pornită!');
  };
  const finish = (id: string) => {
    setInterventions(p => p.map(iv => iv.id === id ? { ...iv, status: 'finalizata', finishedAt: new Date().toISOString() } : iv));
    logActivity('Intervenție finalizată', 'Mentenanță', id);
    message.success('Intervenție finalizată!');
  };
  const addNew = (iv: Intervention) => {
    setInterventions(p => [iv, ...p]);
    logActivity('Intervenție planificată', 'Mentenanță', iv.sensorName);
  };

  const filtered = filterSt === 'all' ? interventions : interventions.filter(iv => iv.status === filterSt);

  const planificate = interventions.filter(iv => iv.status === 'planificata').length;
  const inCurs      = interventions.filter(iv => iv.status === 'in_curs').length;
  const finalizate  = interventions.filter(iv => iv.status === 'finalizata').length;
  const critice     = interventions.filter(iv => iv.priority === 'critical' && iv.status !== 'finalizata').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Mentenanță</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {planificate} planificate · {inCurs} în curs · {finalizate} finalizate
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['list', 'gantt'] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '7px 14px', borderRadius: 9, fontSize: 12, fontWeight: view === v ? 700 : 500, border: `1px solid ${view === v ? '#6366f1' : '#e2e8f0'}`, background: view === v ? '#eef2ff' : '#fff', color: view === v ? '#6366f1' : '#64748b', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
              {v === 'list' ? <><UnorderedListOutlined /> Listă</> : <><CalendarOutlined /> Gantt</>}
            </button>
          ))}
          <button onClick={() => setAddOpen(true)} style={{ padding: '7px 16px', borderRadius: 9, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            <PlusOutlined /> Planifică nouă
          </button>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Planificate',  value: planificate, color: '#6366f1', bg: '#eef2ff',  icon: <CalendarOutlined /> },
          { label: 'În curs',      value: inCurs,      color: '#f59e0b', bg: '#fef3c7',  icon: <ClockCircleOutlined /> },
          { label: 'Finalizate',   value: finalizate,  color: '#10b981', bg: '#d1fae5',  icon: <CheckCircleOutlined /> },
          { label: 'Prioritate critică', value: critice, color: '#ef4444', bg: '#fee2e2', icon: <FireOutlined /> },
        ].map((k, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: k.color }}>{k.icon}</div>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{k.label}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Gantt view */}
      {view === 'gantt' && <GanttView interventions={interventions} />}

      {/* List view */}
      {view === 'list' && (
        <>
          {/* Filtre */}
          <div style={{ display: 'flex', gap: 8 }}>
            {(['all', 'planificata', 'in_curs', 'finalizata', 'anulata'] as const).map(s => (
              <button key={s} onClick={() => setFilterSt(s)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 11, fontWeight: filterSt === s ? 700 : 500, border: `1px solid ${filterSt === s ? '#6366f1' : '#e2e8f0'}`, background: filterSt === s ? '#eef2ff' : '#fff', color: filterSt === s ? '#6366f1' : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                {s === 'all' ? `Toate (${interventions.length})` : `${STAT_LABEL[s]} (${interventions.filter(iv => iv.status === s).length})`}
              </button>
            ))}
          </div>

          {/* Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {filtered.map(iv => (
              <InterventionCard key={iv.id} iv={iv} onStart={start} onFinish={finish} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
              <ToolOutlined style={{ fontSize: 32, display: 'block', marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>Nicio intervenție în categoria selectată</div>
            </div>
          )}
        </>
      )}

      <AddModal open={addOpen} onClose={() => setAddOpen(false)} onAdd={addNew} />
    </div>
  );
};

export default MaintenancePanel;